"use client";

import {
  faBriefcase,
  faCreditCardBlank,
  faMagnifyingGlass,
  faMoneyCheckDollarPen,
  faPiggyBank,
  faUserCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePathname } from "next/navigation";
import { Fragment, useEffect, useRef, useState } from "react";

import Menu from "@client/components/site-header--financial-education/components/menu";
import MenuItem from "@client/components/site-header--financial-education/components/menu-item";
import LogoutMenuItem from "@client/components/site-header--financial-education/components/logout-menu-item";
import Nav from "@client/components/site-header--financial-education/components/nav";
import Search from "@client/components/site-header--financial-education/components/search";
import Toggle from "@client/components/site-header--financial-education/components/toggle";

interface Props {
  hideNavigation?: boolean;
  isLoggedIn?: boolean;
}

export default function DomainNavigation({
  hideNavigation,
  isLoggedIn,
}: Props) {
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [isMenuHidden, setIsMenuHidden] = useState(true);
  const [isToggleHidden, setIsToggleHidden] = useState(false);
  const [showInMenuBar, setShowInMenuBar] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleAriaHidden = () => {
      const $nav = document.getElementById("main-navigation");
      if ($nav) {
        setIsMenuHidden(getComputedStyle($nav).display === "none");
      }

      const $toggle = document.getElementById("main-navigation-toggle");
      if ($toggle) {
        setIsToggleHidden(getComputedStyle($toggle).display === "none");
      }
    };

    // run on 'init'
    handleAriaHidden();

    // run on 'resize'
    window.addEventListener("resize", handleAriaHidden);

    // clear 'resize' event listener
    return () => {
      window.removeEventListener("resize", handleAriaHidden);
    };
  }, []);

  useEffect(() => {
    if (!isNavExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsNavExpanded(false);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (
        navRef.current &&
        !navRef.current.contains(event.relatedTarget as Node)
      ) {
        setIsNavExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    navRef.current?.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      navRef.current?.removeEventListener("focusout", handleFocusOut);
    };
  }, [isNavExpanded]);

  const path = usePathname();

  useEffect(() => {
    // Defer menu bar visibility until after the page has rendered
    // This prevents menu items from appearing/disappearing during navigation
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let attempts = 0;
    const maxAttempts = 20;
    let previousValue: boolean | null = null;
    let isComplete = false;

    const checkPageReady = () => {
      if (isComplete) return;

      attempts++;

      const mainElement = document.querySelector("main");
      const dataSpecPage = mainElement?.getAttribute("data-spec-page");
      const hasValidPath =
        path.includes("resources") || path.includes("category");
      const shouldShow = !!(
        (hasValidPath && dataSpecPage?.includes("financial-education-post")) ||
        dataSpecPage?.includes("financial-education-taxonomy")
      );

      // Only update state if value has changed
      if (previousValue !== shouldShow) {
        setShowInMenuBar(shouldShow);
        previousValue = shouldShow;
      }

      // Stop checking if we have a valid spec or reached max attempts
      if (shouldShow || attempts >= maxAttempts) {
        isComplete = true;
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    // Check immediately
    checkPageReady();

    // Only set up interval if not complete and no interval exists
    if (!isComplete && !intervalId) {
      intervalId = setInterval(checkPageReady, 100);
    }

    // Cleanup function
    return () => {
      isComplete = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [path]);

  if (hideNavigation) {
    return;
  }

  const menuBarItems = showInMenuBar
    ? [
        <MenuItem
          href="/resources/debt-management"
          icon={
            <FontAwesomeIcon
              icon={faCreditCardBlank}
              className={`site-header--financial-education__icon`}
            />
          }
          label="Debt Management"
          variant="button"
          key="debt-management"
        />,
        <MenuItem
          href="/resources/budgeting"
          icon={
            <FontAwesomeIcon
              icon={faMoneyCheckDollarPen}
              className="site-header--financial-education__icon"
            />
          }
          label="Budgeting"
          variant="button"
          key="budgeting"
        />,
        <MenuItem
          href="/resources/savings"
          icon={
            <FontAwesomeIcon
              icon={faPiggyBank}
              className="site-header--financial-education__icon"
            />
          }
          label="Savings"
          variant="button"
          key="savings"
        />,
        <MenuItem
          href="/resources/life-and-career"
          icon={
            <FontAwesomeIcon
              icon={faBriefcase}
              className="site-header--financial-education__icon"
            />
          }
          label="Life & Career"
          variant="button"
          key="life-and-career"
        />,
      ]
    : [];
  return (
    <Nav ref={navRef}>
      <Menu ariaLabel="Main menu" depth={0}>
        {/* {menuBarItems} */}
        {[
          <Fragment key="secondary-menu">
            <Toggle
              ariaControls="hamburger-menu"
              isAriaHidden={isToggleHidden}
              isNavExpanded={isNavExpanded}
              onClick={() => setIsNavExpanded(!isNavExpanded)}
            />
            <Menu
              ariaLabel="Secondary menu"
              depth={1}
              id="hamburger-menu"
              isAriaHidden={isMenuHidden}
              isNavExpanded={isNavExpanded}
              onClick={() => setIsNavExpanded(false)}
              onMouseLeave={() => setIsNavExpanded(false)}
            >
              <MenuItem
                href="/resources/debt-management"
                label="Debt Management"
                key="debt-management"
              />
              <MenuItem
                href="/resources/budgeting"
                label="Budgeting"
                key="budgeting"
              />
              <MenuItem
                href="/resources/savings"
                label="Savings"
                key="savings"
              />
              <MenuItem
                href="/resources/life-and-career"
                label="Life & Career"
                key="life-and-career"
              />
              <MenuItem href={`/about`} label="About" key="about" />
              <MenuItem href={`/contact`} label="Contact" key="contact" />
              <MenuItem
                href="/search"
                icon={
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="site-header--financial-education__icon"
                  />
                }
                label="Search"
                key="search"
              />
              {isLoggedIn ? (
                <LogoutMenuItem
                  hiddenOnMobile={false}
                  key="log-out-mobile"
                />
              ) : (
                <MenuItem
                  href="/log-in"
                  icon={
                    <FontAwesomeIcon
                      icon={faUserCircle}
                      className="site-header--financial-education__icon"
                    />
                  }
                  label="Log In"
                  key="log-in-mobile"
                />
              )}
            </Menu>
          </Fragment>,
          <MenuItem
            href="/search"
            icon={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="site-header--financial-education__icon"
              />
            }
            label="Search"
            variant="button"
            key="search"
            hiddenOnMobile
          />,
          isLoggedIn ? (
            <LogoutMenuItem key="log-out" variant="button" />
          ) : (
            <MenuItem
              href="/log-in"
              icon={
                <FontAwesomeIcon
                  icon={faUserCircle}
                  className="site-header--financial-education__icon"
                />
              }
              label="Log In"
              variant="button"
              key="log-in"
              hiddenOnMobile
            />
          ),
        ]}
      </Menu>
    </Nav>
  );
}
